/**
 * Authentication component styles - layout and interactive states.
 * Colors come from MUI theme.
 *
 * NOTE: TextField styles are now centralized in src/styles/textFieldStyles.ts
 * Import textFieldPrimary, textFieldWarning, etc. from there.
 */

import { SxProps, Theme } from '@mui/material';

export const authStyles = {
  // Login form styles.
  loginCard: {
    width: '100%',
    maxWidth: 340,
    margin: '0 auto',
    minHeight: 'fit-content',
    backgroundColor: 'background.paper',
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 'custom.subtleShadow',
    overflow: 'visible',
  } as SxProps<Theme>,
  loginHeader: {
    p: 3,
    pb: 2,
    textAlign: 'center',
    borderBottom: '1px solid',
    borderColor: 'divider',
  } as SxProps<Theme>,
  loginIconCircle: {
    margin: '0 auto 16px',
  } as SxProps<Theme>,
  loginTitle: {
    mb: 0.5,
    fontSize: '17px',
  } as SxProps<Theme>,
  loginSubtitle: {
    fontSize: '14px',
  } as SxProps<Theme>,
  loginForm: {
    display: 'flex',
    flexDirection: 'column',
    p: 3,
    gap: 2,
  } as SxProps<Theme>,
  textFieldOverride: {} as SxProps<Theme>,
  visibilityToggle: {
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  } as SxProps<Theme>,
  loginSubmitButton: {
    mt: 1,
    py: 1.5,
    textTransform: 'none',
    fontSize: '16px',
    fontWeight: 600,
    borderRadius: 1.5,
    boxShadow: 'custom.cardShadow',
    '&:hover': {
      backgroundColor: 'primary.dark',
      boxShadow: 'custom.dropdownShadow',
    },
    '&:disabled': {
      backgroundColor: 'action.disabledBackground',
      boxShadow: 'none',
    },
  } as SxProps<Theme>,
  loginErrorAlert: {
    mt: 1,
    borderRadius: 1.5,
  } as SxProps<Theme>,
  inputIcon: {
    color: 'text.secondary',
    fontSize: 20,
  } as SxProps<Theme>,

  // New password form styles.
  newPasswordCard: {
    width: '100%',
    maxWidth: 380,
    margin: '0 auto',
    minHeight: 'fit-content',
    backgroundColor: 'background.paper',
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'warning.light',
    boxShadow: 'custom.subtleShadow',
    overflow: 'visible',
  } as SxProps<Theme>,
  newPasswordHeader: {
    p: 3,
    pb: 2,
    textAlign: 'center',
    borderBottom: '1px solid',
    borderColor: 'divider',
  } as SxProps<Theme>,
  newPasswordBackButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    mb: 2,
  } as SxProps<Theme>,
  newPasswordBackButton: {
    color: 'text.secondary',
    '&:hover': {
      color: 'primary.main',
      backgroundColor: 'action.hover',
    },
  } as SxProps<Theme>,
  newPasswordIconCircleWarning: {
    backgroundColor: 'warning.main',
    margin: '0 auto 16px',
  } as SxProps<Theme>,
  newPasswordTitle: {
    fontWeight: 600,
    color: 'warning.main',
    mb: 0.5,
    fontSize: '17px',
  } as SxProps<Theme>,
  newPasswordSubtitle: {
    fontSize: '14px',
  } as SxProps<Theme>,
  newPasswordForm: {
    display: 'flex',
    flexDirection: 'column',
    p: 3,
    gap: 2,
  } as SxProps<Theme>,
  textFieldWarning: {} as SxProps<Theme>,
  newPasswordRequirementsBox: {
    p: 2,
    borderRadius: 1.5,
    bgcolor: 'custom.warningBgLight',
    border: 1,
    borderColor: 'custom.warningBorderLight',
  } as SxProps<Theme>,
  newPasswordRequirementsTitle: {
    fontWeight: 600,
    color: 'warning.main',
    mb: 1,
    fontSize: '14px',
  } as SxProps<Theme>,
  newPasswordRequirementRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    py: 0.5,
    transition: 'all 0.3s ease',
  } as SxProps<Theme>,
  newPasswordAlertStyle: {
    borderRadius: 1.5,
  } as SxProps<Theme>,
  newPasswordSubmitButton: {
    mt: 1,
    py: 1.5,
    textTransform: 'none',
    fontSize: '16px',
    fontWeight: 600,
    borderRadius: 1.5,
    boxShadow: 'custom.cardShadow',
    '&:hover': {
      backgroundColor: 'warning.dark',
      boxShadow: 'custom.dropdownShadow',
    },
    '&:disabled': {
      backgroundColor: 'action.disabledBackground',
      boxShadow: 'none',
    },
  } as SxProps<Theme>,

  // No-access page styles.
  noAccessContainer: {
    height: '100%',
    width: '100%',
    p: 2,
    overflow: 'hidden',
    boxSizing: 'border-box',
  } as SxProps<Theme>,
  noAccessCard: {
    p: 3,
    textAlign: 'center',
    maxWidth: 380,
    width: '90%',
    border: '1px solid',
    borderColor: 'error.light',
    borderRadius: 2,
    boxSizing: 'border-box',
  } as SxProps<Theme>,
  noAccessIconCircleError: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: 'error.main',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  } as SxProps<Theme>,
  noAccessTitle: {
    fontWeight: 600,
    color: 'error.main',
    mb: 2,
  } as SxProps<Theme>,
  noAccessBodyText: {
    mb: 2,
    lineHeight: 1.5,
    px: 1,
    color: 'text.secondary',
  } as SxProps<Theme>,
  noAccessHelpBox: {
    mt: 3,
    p: 2,
    backgroundColor: 'custom.backgroundAlt',
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    flexWrap: 'wrap',
    gap: 1,
  } as SxProps<Theme>,
  noAccessHelpText: {
    textAlign: 'center',
    lineHeight: 1.4,
  } as SxProps<Theme>,

  // Style utility functions.
  getSubmitButtonBgColor: (challengeExpired: boolean) =>
    challengeExpired ? 'warning.dark' : 'warning.main',
};
